// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useState } from 'react';

import { Button, Divider, Flex, Loader, Stack, Text } from '@mantine/core';

import { StatusPoint } from 'components/Mantine/StatusPoint';

import type { CustomStatusEntityName, StateNameFor, StatusIdFor } from './types';
import { useCustomStatuses } from './useCustomStatuses';

type Props<TEntity extends CustomStatusEntityName> = {
  customStatusTypeName: TEntity;
  stateName: StateNameFor<TEntity>;
  currentStatusId: StatusIdFor<TEntity> | null;
  /** When provided, renders a "Manage statuses" button below the list that calls this. */
  onOpenManage?: () => void;
  /** Called with the chosen status id (or `null` to clear). */
  onAssign: (id: StatusIdFor<TEntity> | null) => Promise<void>;
};

const StatusButton = ({
  label,
  loading,
  disabled,
  onClick,
  leftSection,
}: {
  label: string;
  loading: boolean;
  disabled?: boolean;
  onClick: () => void;
  leftSection: ReactNode;
}) => (
  <Button
    variant="white"
    c="dark"
    bg={loading ? 'gray.0' : undefined}
    onClick={disabled || loading ? undefined : onClick}
    flex="0 0 auto"
    leftSection={loading ? <Loader size={10} /> : leftSection}
    style={{ cursor: disabled || loading ? 'initial' : 'pointer' }}
    styles={{ inner: { justifyContent: 'flex-start' } }}
  >
    <Text>{label}</Text>
  </Button>
);

/**
 * Popover contents for assigning a custom status to an entity instance.
 */
export const StatusPickerContents = <TEntity extends CustomStatusEntityName>({
  customStatusTypeName,
  stateName,
  currentStatusId,
  onOpenManage,
  onAssign,
}: Props<TEntity>) => {
  const { stateColorMap, states } = useCustomStatuses(customStatusTypeName);
  const currentStateStatuses = states?.find((state) => state.name === stateName)?.statuses ?? [];

  const [assigningStatusId, setAssigningStatusId] = useState<StatusIdFor<TEntity> | null | undefined>();

  const handleAssign = (id: StatusIdFor<TEntity> | null) => {
    if (id === currentStatusId) return;
    setAssigningStatusId(id);
    void onAssign(id).finally(() => setAssigningStatusId(undefined));
  };

  const { color, label: stateLabel } = stateColorMap[stateName];

  return (
    <Stack p={0} gap={0} miw={200}>
      <Stack p="xs" gap={2} mah={300} style={{ overflowY: 'auto' }}>
        <StatusButton
          label={stateLabel}
          loading={assigningStatusId === null}
          disabled={assigningStatusId !== undefined}
          leftSection={<StatusPoint color={color} />}
          onClick={() => handleAssign(null)}
        />
        <Stack gap={2}>
          {currentStateStatuses.map((status) => (
            <StatusButton
              key={status.statusId}
              label={status.name}
              loading={assigningStatusId === status.statusId}
              disabled={assigningStatusId !== undefined}
              leftSection={<StatusPoint color={status.color} />}
              onClick={() => handleAssign(status.statusId)}
            />
          ))}
        </Stack>
      </Stack>
      {onOpenManage && (
        <>
          <Divider w="100%" />
          <Flex p="xs">
            <Button fullWidth size="compact-sm" variant="subtle" onClick={onOpenManage}>
              Manage statuses
            </Button>
          </Flex>
        </>
      )}
    </Stack>
  );
};
